import cn from 'clsx';
import PropTypes from 'prop-types';
import {useMemo} from 'react';

import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, Separator, Text} from '../../../ui';
import {PROFILE_MARKETING_STATUSES} from '../constants/profileMarketingStatuses';
import BecomeCreatorWidget from '../containers/BecomeCreatorWidget';
import css from '../styles/AccountSettingsWidgetLayout.module.css';

export const ACCOUNT_SETTINGS_WIDGET_TEST_IDS = {
  CHANGE_PASSWORD: 'changePassword',
  EMAIL_NOTIFICATIONS: 'emailNotifications',
  DELETE_ACCOUNT: 'deleteAccount',
};

const getEmailNotificationButtonNode = (
  marketingSubscriptionStatus,
  onEmailSubscriptionButtonClick
) => {
  switch (marketingSubscriptionStatus) {
    case PROFILE_MARKETING_STATUSES.NOT_SUBSCRIBED: {
      return (
        <Button
          color={Button.COLOR.SECONDARY}
          height={Button.HEIGHT.SMALL}
          onClick={onEmailSubscriptionButtonClick}
        >
          <TextLocal id={'profile.subscribe'} />
        </Button>
      );
    }
    case PROFILE_MARKETING_STATUSES.SUBSCRIBED: {
      return (
        <Button
          color={Button.COLOR.RED}
          height={Button.HEIGHT.SMALL}
          onClick={onEmailSubscriptionButtonClick}
        >
          <TextLocal id={'profile.unsubscribe'} />
        </Button>
      );
    }
    case PROFILE_MARKETING_STATUSES.PENDING: {
      return (
        <div className={css.pendingBlock}>
          <Button
            color={Button.COLOR.YELLOW}
            height={Button.HEIGHT.SMALL}
            onClick={onEmailSubscriptionButtonClick}
          >
            <TextLocal id={'profile.resend'} />
          </Button>
          <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_14}>
            <TextLocal id={'profile.check-inbox-or-spam'} />
          </Text>
        </div>
      );
    }
  }
};

const AccountSettingsWidgetLayout = ({
  onChangePasswordClick,
  onDeleteAccountClick,
  marketingSubscriptionStatus,
  onEmailSubscriptionButtonClick,
  withBecomeCreatorBlock,
  isDeleteAccountPending,
  withTopMargin,
}) => {
  const emailNotificationButtonNode = useMemo(() => {
    return getEmailNotificationButtonNode(
      marketingSubscriptionStatus,
      onEmailSubscriptionButtonClick
    );
  }, [marketingSubscriptionStatus, onEmailSubscriptionButtonClick]);

  return (
    <div className={cn(css.widget, withTopMargin && css.widgetWithTopMargin)}>
      <Text
        fontWeight={Text.FONT_WEIGHT.MEDIUM}
        fontFamily={Text.FONT_FAMILY.RUBIK}
        fontSize={Text.FONT_SIZE.SIZE_32}
      >
        <TextLocal id={'profile.account-settings'} />
      </Text>
      <div
        className={css.changePasswordBlock}
        data-testid={ACCOUNT_SETTINGS_WIDGET_TEST_IDS.CHANGE_PASSWORD}
      >
        <div className={css.changePasswordDescription}>
          <Text
            fontSize={Text.FONT_SIZE.SIZE_18}
            fontWeight={Text.FONT_WEIGHT.BOLD}
          >
            <TextLocal id={'profile.change-password'} />
          </Text>
          <Text fontSize={Text.FONT_SIZE.SIZE_14} color={Text.COLOR.GREY}>
            <TextLocal id={'profile.confirm-your-new-password'} />
          </Text>
        </div>
        <Button
          color={Button.COLOR.SECONDARY}
          height={Button.HEIGHT.SMALL}
          onClick={onChangePasswordClick}
          border={Button.BORDER.BOLD}
        >
          <TextLocal id={'profile.change-password'} />
        </Button>
      </div>
      <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
      {withBecomeCreatorBlock && <BecomeCreatorWidget />}
      <div
        className={css.emailNotificationsBlock}
        data-testid={ACCOUNT_SETTINGS_WIDGET_TEST_IDS.EMAIL_NOTIFICATIONS}
      >
        <div className={css.emailNotificationsDescription}>
          <Text
            fontWeight={Text.FONT_WEIGHT.BOLD}
            fontSize={Text.FONT_SIZE.SIZE_18}
            color={Text.COLOR.PRIMARY}
          >
            <TextLocal id={'profile.email-notifications'} />
          </Text>
          <Text
            color={Text.COLOR.GREY}
            fontSize={Text.FONT_SIZE.SIZE_14}
            lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_21}
          >
            <TextLocal id={'profile.email-notifications-description'} />
          </Text>
        </div>
        <div className={css.emailNotificationButtonBlock}>
          {emailNotificationButtonNode}
        </div>
      </div>
      <>
        <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
        <div
          className={css.deleteAccountBlock}
          data-testid={ACCOUNT_SETTINGS_WIDGET_TEST_IDS.DELETE_ACCOUNT}
        >
          <div className={css.deleteAccountDescription}>
            <Text
              fontWeight={Text.FONT_WEIGHT.BOLD}
              fontSize={Text.FONT_SIZE.SIZE_18}
            >
              <TextLocal id={'profile.delete-my-account'} />
            </Text>
            <Text color={Text.COLOR.GREY} fontSize={Text.FONT_SIZE.SIZE_14}>
              <TextLocal id={'profile.if-you-delete-your-account'} />
            </Text>
          </div>
          {isDeleteAccountPending !== null &&
            (isDeleteAccountPending ? (
              <div className={css.pendingButton}>
                <Button
                  color={Button.COLOR.YELLOW}
                  height={Button.HEIGHT.SMALL}
                >
                  <TextLocal id={'profile.pending'} />
                </Button>
              </div>
            ) : (
              <Button
                color={Button.COLOR.RED}
                height={Button.HEIGHT.SMALL}
                onClick={onDeleteAccountClick}
                border={Button.BORDER.BOLD}
              >
                <TextLocal id={'profile.delete-account'} />
              </Button>
            ))}
        </div>
      </>
    </div>
  );
};

AccountSettingsWidgetLayout.propTypes = {
  onChangePasswordClick: PropTypes.func.isRequired,
  onDeleteAccountClick: PropTypes.func.isRequired,
  marketingSubscriptionStatus: PropTypes.string.isRequired,
  onEmailSubscriptionButtonClick: PropTypes.func.isRequired,
  withBecomeCreatorBlock: PropTypes.bool.isRequired,
  withTopMargin: PropTypes.bool.isRequired,
  isDeleteAccountPending: PropTypes.bool.isRequired,
};

export default AccountSettingsWidgetLayout;
